import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, authAxios } from '../contexts/AuthContext';
import { Search, Plus, LogOut, ChevronRight, ClipboardList, KeyRound, Eye, EyeOff, Clock, Users, Package, AlertCircle, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('search');

  useEffect(() => {
    authAxios.get(`${API}/dashboard/stats`).then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try { const { data } = await authAxios.get(`${API}/clients/search?q=${encodeURIComponent(searchQuery)}`); setResults(data); }
      catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.new_pw) { toast.error('Fill all fields'); return; }
    if (pwForm.new_pw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await authAxios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.new_pw });
      toast.success('Password changed!');
      setShowPasswordDialog(false);
      setPwForm({ current: '', new_pw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setPwLoading(false);
  };

  const statusColor = (s) => ({ new:'bg-blue-50 text-blue-700', in_progress:'bg-amber-50 text-amber-700', trial:'bg-purple-50 text-purple-700', completed:'bg-green-50 text-green-700', delivered:'bg-[#F2F2F7] text-[#8E8E93]' }[s] || 'bg-[#F2F2F7] text-[#8E8E93]');

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      {/* Header */}
      <header className="bg-white sticky top-0 z-10 border-b border-[#E5E5EA]">
        <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <Link to="/dashboard" className="shrink-0">
            <img src="/logo.png" alt="Payal Garg" className="h-7 sm:h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5">
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button size="sm" className="bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-[11px] h-7 px-3" data-testid="go-admin-btn">Admin</Button>
              </Link>
            )}
            <Button variant="ghost" size="sm" className="text-[#8E8E93] rounded-full h-7 w-7 p-0" data-testid="change-password-btn" onClick={() => setShowPasswordDialog(true)}>
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
            <Button data-testid="logout-btn" variant="ghost" size="sm" className="text-[#8E8E93] hover:text-red-500 h-7 w-7 p-0 rounded-full" onClick={async () => { await logout(); navigate('/'); }}>
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6 flex-1 w-full">
        {/* Welcome + Quick Stats */}
        {stats && (
          <div className="mb-5">
            <h1 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] mb-3">Hi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
            <div className="grid grid-cols-3 gap-2.5">
              <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
                <Users className="w-4 h-4 text-[#8E8E93] mx-auto mb-1" strokeWidth={1.5} />
                <p className="text-xl font-bold text-[#1C1C1E]">{stats.total_clients}</p>
                <p className="text-[10px] text-[#8E8E93] uppercase tracking-wider">Clients</p>
              </div>
              <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
                <Package className="w-4 h-4 text-[#8E8E93] mx-auto mb-1" strokeWidth={1.5} />
                <p className="text-xl font-bold text-[#1C1C1E]">{stats.pending_orders}</p>
                <p className="text-[10px] text-[#8E8E93] uppercase tracking-wider">Pending</p>
              </div>
              <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
                <Clock className="w-4 h-4 text-[#8E8E93] mx-auto mb-1" strokeWidth={1.5} />
                <p className="text-xl font-bold text-[#1C1C1E]">{stats.todays_orders}</p>
                <p className="text-[10px] text-[#8E8E93] uppercase tracking-wider">Today</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-1 mb-4 bg-[#E5E5EA] rounded-full p-0.5">
          {[
            { key: 'search', label: 'Search', icon: Search },
            { key: 'pending', label: 'Pending', icon: AlertCircle },
            { key: 'recent', label: 'Recent', icon: Clock },
          ].map(tab => (
            <button key={tab.key} data-testid={`tab-${tab.key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-[13px] font-medium transition-all ${
                activeTab === tab.key ? 'bg-white text-[#1C1C1E] shadow-sm' : 'text-[#8E8E93]'
              }`}
              onClick={() => setActiveTab(tab.key)}>
              <tab.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" strokeWidth={1.5} />
                <Input data-testid="dashboard-search-input"
                  className="h-12 pl-10 pr-4 text-base bg-white border-0 shadow-sm text-[#1C1C1E] placeholder:text-[#C7C7CC] focus:ring-1 focus:ring-[#D1D1D6] rounded-2xl"
                  placeholder="Name or phone..."
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
              <Button data-testid="new-order-btn" className="h-12 w-12 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl p-0 shrink-0" onClick={() => navigate('/order/new')}>
                <Plus className="w-5 h-5" strokeWidth={2} />
              </Button>
            </div>

            {searching && <p className="text-sm text-[#8E8E93]">Searching...</p>}

            {results.length > 0 && (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                {results.map((client, i) => (
                  <div key={client.id}
                    className={`px-4 py-3.5 flex items-center justify-between cursor-pointer active:bg-[#F5F5F5] ${i < results.length - 1 ? 'border-b border-[#F2F2F7]' : ''}`}
                    onClick={() => navigate(`/client/${client.id}`)}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-[#F2F2F7] flex items-center justify-center shrink-0">
                        <span className="text-sm font-semibold text-[#1C1C1E]">{client.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[15px] font-semibold text-[#1C1C1E] truncate">{client.name}</p>
                        <p className="text-[13px] text-[#8E8E93]">{client.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button data-testid={`quick-order-${client.id}`} size="sm" className="bg-[#F2F2F7] text-[#1C1C1E] hover:bg-[#E5E5EA] rounded-full text-[11px] h-7 px-2.5 font-medium"
                        onClick={(e) => { e.stopPropagation(); navigate(`/order/new/${client.id}`); }}>
                        <Plus className="w-3 h-3 mr-0.5" strokeWidth={2} />Order
                      </Button>
                      <ChevronRight className="w-4 h-4 text-[#C7C7CC]" strokeWidth={1.5} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && results.length === 0 && !searching && (
              <div className="bg-white rounded-2xl text-center p-6 shadow-sm">
                <p className="text-[#8E8E93] text-sm mb-3">No clients found</p>
                <Button data-testid="dashboard-add-client-btn" className="h-9 px-5 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm" onClick={() => navigate(`/order/new?name=${encodeURIComponent(searchQuery)}`)}>
                  Add New Client
                </Button>
              </div>
            )}

            {searchQuery.length < 2 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-3 shadow-sm">
                  <Search className="w-5 h-5 text-[#C7C7CC]" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-[#8E8E93]">Search for a client</p>
              </div>
            )}
          </>
        )}

        {/* Pending/Trials Tab */}
        {activeTab === 'pending' && (
          <>
            <h2 className="text-[13px] font-medium text-[#8E8E93] uppercase tracking-wider mb-2">Orders needing attention</h2>
            {stats?.trial_orders?.length > 0 ? (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                {stats.trial_orders.map((order, i) => (
                  <div key={order.id}
                    className={`px-4 py-3.5 flex items-center justify-between cursor-pointer active:bg-[#F5F5F5] ${i < stats.trial_orders.length - 1 ? 'border-b border-[#F2F2F7]' : ''}`}
                    onClick={() => navigate(`/client/${order.client_id}`)}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-[#1C1C1E]">#{order.order_number}</span>
                        <Badge className={`${statusColor(order.status)} border-0 text-[10px] capitalize`}>{order.status?.replace('_',' ')}</Badge>
                      </div>
                      <p className="text-[13px] text-[#8E8E93] mt-0.5">{order.client_name} &middot; {order.garment_type?.replace('_',' ')}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#C7C7CC] shrink-0" strokeWidth={1.5} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl text-center p-6 shadow-sm">
                <p className="text-[#8E8E93] text-sm">No pending orders</p>
              </div>
            )}
          </>
        )}

        {/* Recent Tab */}
        {activeTab === 'recent' && (
          <>
            <h2 className="text-[13px] font-medium text-[#8E8E93] uppercase tracking-wider mb-2">Recently updated</h2>
            {stats?.recent_orders?.length > 0 ? (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                {stats.recent_orders.map((order, i) => (
                  <div key={order.id}
                    className={`px-4 py-3.5 flex items-center justify-between cursor-pointer active:bg-[#F5F5F5] ${i < stats.recent_orders.length - 1 ? 'border-b border-[#F2F2F7]' : ''}`}
                    onClick={() => navigate(`/client/${order.client_id}`)}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-[#1C1C1E]">#{order.order_number}</span>
                        <Badge className={`${statusColor(order.status)} border-0 text-[10px] capitalize`}>{order.status?.replace('_',' ')}</Badge>
                      </div>
                      <p className="text-[13px] text-[#8E8E93] mt-0.5">{order.client_name} &middot; {order.garment_type?.replace('_',' ')}</p>
                    </div>
                    <span className="text-[11px] text-[#C7C7CC] shrink-0">{new Date(order.updated_at || order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl text-center p-6 shadow-sm">
                <p className="text-[#8E8E93] text-sm">No recent orders</p>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-white max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Change Password</DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">Set a new password for your account</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Current Password</Label>
              <Input type="password" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl" value={pwForm.current} onChange={(e) => setPwForm(f => ({ ...f, current: e.target.value }))} placeholder="Enter current password" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">New Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} className="h-12 bg-[#F2F2F7] border-0 rounded-2xl pr-12" value={pwForm.new_pw} onChange={(e) => setPwForm(f => ({ ...f, new_pw: e.target.value }))} placeholder="New password" />
                <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93]" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Confirm Password</Label>
              <Input type="password" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl" value={pwForm.confirm} onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Confirm new password" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-12 border-[#E5E5EA] rounded-full text-sm" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
              <Button data-testid="save-new-password-btn" className="flex-1 h-12 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-semibold" onClick={handleChangePassword} disabled={pwLoading}>
                {pwLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
